'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getPrimaryRole } from '@/lib/auth/getPrimaryRole';
import { useAppUser } from '@/lib/context/AppUserContext';
import { UserRole } from '@/lib/enums';
import { trackingManager } from '@/lib/tracking/tracking-manager-instance';

import { CommandPaletteContextValue } from '../types';
import { CommandPalette } from './command-palette';

export const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export function useCommandPalette(): CommandPaletteContextValue {
  const context = useContext(CommandPaletteContext);
  if (!context) {
    throw new Error('useCommandPalette must be used within a CommandProvider');
  }
  return context;
}

interface CommandProviderProps {
  children: React.ReactNode;
}

export function CommandProvider({ children }: CommandProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const wasOpenRef = useRef(false);
  const { user } = useAppUser();

  // Command palette is admin-only. The Quick Search button, the Cmd+K
  // shortcut and the palette UI itself are all gated on this flag.
  const isAdmin = useMemo(() => {
    if (!user?.role) return false;
    const roles = Array.isArray(user.role) ? user.role : [user.role];
    return roles.includes(UserRole.admin);
  }, [user?.role]);

  const open = useCallback(() => {
    if (isAdmin) setIsOpen(true);
  }, [isAdmin]);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback(() => {
    if (isAdmin) setIsOpen((prev) => !prev);
  }, [isAdmin]);

  // Global keyboard shortcut: ⌘K (Mac) or Ctrl+K (Windows/Linux) — admin-only.
  useEffect(() => {
    if (!isAdmin) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        // Track opening via keyboard (only when opening, not closing)
        if (!wasOpenRef.current) {
          trackingManager.trackEvent('COMMAND_PALETTE_OPENED', {
            trigger: 'keyboard',
            role: getPrimaryRole(user?.role),
          });
        }
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggle, isAdmin, user?.role]);

  // Track isOpen state for keyboard toggle detection
  useEffect(() => {
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const contextValue = useMemo(
    () => ({ isOpen, open, close, toggle, isAdmin }),
    [isOpen, open, close, toggle, isAdmin],
  );

  return (
    <CommandPaletteContext.Provider value={contextValue}>
      {children}
      {isAdmin && <CommandPalette />}
    </CommandPaletteContext.Provider>
  );
}
